import React from "react";
import { Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";

/**
 * Blog post: What is education for?
 * A single, focused editorial page rendered in the site's classical
 * long-form style — matching the Academics narrative treatment.
 */
const WhatIsEducationFor = () => {
  return (
    <PageLayout>
      <article className="bg-[#fbf7ee]" data-testid="blog-what-is-education-for">
        <div className="max-w-[820px] mx-auto px-6 md:px-10 lg:px-14 pt-20 md:pt-28 pb-20 md:pb-24">
          {/* Title */}
          <div className="text-center mb-6 md:mb-8">
            <h1
              className="font-display text-[#1e2952] text-[36px] md:text-[54px] leading-[1.08]"
              style={{ fontWeight: 500 }}
            >
              What is the Purpose of Education?
            </h1>
            <div className="mt-7 flex items-center justify-center gap-3">
              <span className="block h-px w-40 bg-[#c9a961]"></span>
              <span className="text-[#c9a961]">†</span>
              <span className="block h-px w-40 bg-[#c9a961]"></span>
            </div>
          </div>

          {/* Body */}
          <div className="font-serif-body text-[17px] md:text-[18px] leading-[1.9] text-[#3a466e] space-y-6">
            <p className="italic">
              When forming a curriculum the first question we must ask is, What is the purpose of education?
            </p>

            <p>
              For a Catholic school, the answer begins with the end for which the human person was created. As St. Augustine wrote, <em>"You have made us for Yourself, O Lord, and our heart is restless until it rests in You."</em> Education must therefore aim at more than knowledge, skill, or worldly success. It should form the intellect to recognize truth, the will to choose the good, and the heart to love what is worthy of love, preparing the student to <em>"know Him, love Him, and serve Him in this world, so as to be happy with Him forever in heaven."</em>
            </p>

            <p>
              In the Catholic tradition, <em>wisdom</em> is the highest intellectual virtue — the habit of considering all things in the light of their first cause, God — and, as St. Thomas Aquinas teaches, <em>"it belongs to the wise to order things rightly."</em> An education ordered toward wisdom is therefore not merely the accumulation of information, but the ordering of the whole person — intellect, will, imagination, and heart — toward the truth about God and the goodness of His creation.
            </p>

            <p>
              The question governing our curriculum is therefore not simply <em>What must our students know?</em> but <em>What course of study and formation will best prepare them to live faithfully, reason soundly, grow in wisdom and virtue, and ultimately behold God?</em>
            </p>

            <p>
              The Church's intellectual tradition gives us a clear framework. Saints such as St. Thomas Aquinas, St. Bonaventure, St. Ignatius of Loyola, and St. Thomas More were formed through rigorous study of the Liberal Arts, Philosophy, and Theology. Their intellectual formation disciplined the mind and prepared it for the service of truth, leading them to great holiness.
            </p>

            <p>
              At Angel of Peace, academic excellence is therefore not an end in itself, but part of the formation of the intellect, imagination, and will.
            </p>

            <p>
              This formation begins with reality itself. In the Aristotelian and Thomistic tradition, human knowledge begins in the senses: we first encounter the world by seeing, hearing, touching, observing, and doing, and from these concrete experiences the intellect gradually rises toward understanding. Especially in the early years, students therefore need abundant firsthand encounters with the real world before being hurried into abstraction. Nature study, observation, drawing, measurement, music, handicrafts, beautiful language, and direct experience help furnish the imagination and give the intellect the material from which deeper understanding can grow.
            </p>

            <p>
              By graduation, students should possess more than an accumulation of facts. They should know how to read difficult texts attentively, follow and construct an argument, express themselves clearly, recognize truth and error, investigate the natural world, and approach the highest questions with intellectual humility.
            </p>
          </div>

          {/* Back link */}
          <div className="mt-14 md:mt-16 pt-8 border-t border-[#eae1c4] flex justify-center">
            <Link
              to="/academics"
              data-testid="blog-back-to-curriculum"
              className="inline-flex items-center gap-2 font-sans-nav text-[11.5px] tracking-[0.28em] uppercase text-[#c9a961] hover:text-[#1e2952] transition-colors"
            >
              <span aria-hidden="true">←</span>
              Back to Curriculum
            </Link>
          </div>
        </div>
      </article>
    </PageLayout>
  );
};

export default WhatIsEducationFor;
